import type { Translation } from '@/lib/api/common'

/* -------------------------------------------------------------------------- */
/* Choice groups — priced add-ons a customer picks while ordering              */
/* -------------------------------------------------------------------------- */

/** Whether a customer may pick one option or several. */
export const ChoiceType = {
  Single: 'single',
  Multiple: 'multiple',
} as const

export type ChoiceType = (typeof ChoiceType)[keyof typeof ChoiceType]

export interface ChoiceGroupItem {
  id: string
  name: Translation
  /** Added to the line when picked. Zero is a real answer, not "unpriced". */
  price: number
  sortOrder: number
}

/** A row of the choice-group list. Items arrive only on the detail call. */
export interface ChoiceGroup {
  id: string
  name: Translation
  isRequire: boolean
  selectType: string | null
  sortOrder: number
  minSelect: number | null
  maxSelect: number | null
}

export interface ChoiceGroupDetail extends ChoiceGroup {
  items: ChoiceGroupItem[]
}

/**
 * The create body. Items may travel with it, which is what lets a group be set
 * up in one save rather than a save followed by a row of item calls.
 */
export interface CreateChoiceGroupBody {
  name: Translation
  selectType: string
  isRequire?: boolean
  sortOrder?: number
  items?: SaveChoiceGroupItemBody[]
}

/**
 * The update body, which the server builds from the *read* DTO rather than the
 * create one — so it takes no `items`. Items are edited through their own
 * endpoints.
 */
export interface UpdateChoiceGroupBody {
  name?: Translation
  selectType?: string
  isRequire?: boolean
  sortOrder?: number
  minSelect?: number
  maxSelect?: number
}

export interface SaveChoiceGroupItemBody {
  name: Translation
  price: number
  sortOrder?: number
}

/* -------------------------------------------------------------------------- */
/* Option sets — the axes a product's variants are built from                  */
/* -------------------------------------------------------------------------- */

export interface OptionSetItem {
  id: string
  name: Translation
}

/**
 * A reusable option axis — `Size: S, M, L`.
 *
 * Distinct from a choice group: an option set multiplies out into variants that
 * each have their own SKU and price, while a choice group is an add-on priced
 * on top of one.
 */
export interface OptionSet {
  id: string
  name: Translation
  displayName: Translation | null
  enabled: boolean
  items: OptionSetItem[]
}

export interface SaveOptionSetBody {
  name: Translation
  displayName: Translation
  sortOrder?: number
  items?: { name: Translation }[]
}
